import { Box, Sparkles } from "lucide-react";
import { Button } from "@/ui-components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/ui-components/ui/dialog";
import { useRouter } from "@/hooks/useRouter";
import { minPlanFor, type Feature, type Plan } from "../../../../shared/entitlements";

const FEATURE_LABEL: Record<Feature, string> = {
  resume: "Resume",
  skills: "Skills",
  analytics: "Analytics",
  teams: "Teams",
  knowledge_base: "Knowledge Base",
  work_tracking: "Work Tracking",
  governance: "Governance",
};

const FEATURE_BLURB: Record<Feature, string> = {
  resume: "Pick up any past session right where you left off.",
  skills: "Build, install, and run Skills to automate your AI workflows.",
  analytics:
    "See spend, activity, and prompt quality across every coding agent you use.",
  teams:
    "Create a team to share your workspaces and sessions with teammates.",
  knowledge_base: "Turn your team's sessions into a shared knowledge base.",
  work_tracking: "Track work threads across your team's sessions.",
  governance: "Admin controls, audit, and policy for your organization.",
};

const PLAN_LABEL: Record<Plan, string> = {
  free: "Free",
  pro: "Pro",
  team: "Team",
  enterprise: "Enterprise",
};

// Where to return the user after they upgrade (the page they were trying to
// reach when the gate fired).
const FEATURE_PATH: Partial<Record<Feature, string>> = {
  skills: "/skills",
  teams: "/teams",
};

export function UpgradeBody({ feature }: { feature: Feature }) {
  const router = useRouter();
  const plan = minPlanFor(feature);
  const planLabel = PLAN_LABEL[plan];
  const next = FEATURE_PATH[feature];
  const checkoutHref = `/checkout?plan=${plan}${
    next ? `&next=${encodeURIComponent(next)}` : ""
  }`;

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#01B9D9] to-[#0095B6] flex items-center justify-center text-white">
          <Box className="w-5 h-5" />
        </div>
        <div>
          <p className="text-base font-semibold text-foreground">
            {FEATURE_LABEL[feature]} is a {planLabel} feature
          </p>
          <p className="text-sm text-muted-foreground">
            Available on SpecStory {planLabel} and above.
          </p>
        </div>
      </div>
      <p className="mt-4 text-sm text-muted-foreground">{FEATURE_BLURB[feature]}</p>
      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-2">
        <Button className="gap-2" onClick={() => router.push(checkoutHref)}>
          <Sparkles className="w-4 h-4" />
          Upgrade to {planLabel}
        </Button>
        <a
          href="https://specstory.com/pricing"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline sm:ml-1"
        >
          Compare plans
        </a>
      </div>
    </>
  );
}

// Modal shown when a user clicks a gated entry point (button / menu item).
export function UpgradeDialog({
  feature,
  open,
  onClose,
}: {
  feature: Feature | null;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="sr-only">
          <DialogTitle>Upgrade required</DialogTitle>
          <DialogDescription>
            This feature requires a paid plan.
          </DialogDescription>
        </DialogHeader>
        {feature && <UpgradeBody feature={feature} />}
        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={onClose}>
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Inline upgrade panel — used as a route guard fallback so direct navigation
// to a gated page (e.g. /skills) doesn't expose the feature.
export function UpgradeCard({ feature }: { feature: Feature }) {
  return (
    <div className="w-full max-w-md mx-auto mt-16 px-6">
      <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
        <UpgradeBody feature={feature} />
      </div>
    </div>
  );
}
